import type { Requirement } from '@ben/columbia/api/requirements';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import styled, { css } from 'styled-components';
import { DOMAIN_ORDER, domainLabel } from './domains';
import { Muted, Stack } from './shared';
import type { RequirementStatus, StatusCounts } from './status';
import { mostPressingStatus, StatusDot } from './status';

/**
 * Requirements grouped under the domain of the child group each one sits in.
 *
 * That is what the group tree encodes, so the domains are the headings rather
 * than a list of their own.
 */

export interface DomainTotals {
  total: number;
  outstanding: number;
  unanswered: number;
}

export interface DomainGroup {
  domain: string | null;
  requirements: Requirement[];
}

/**
 * Outstanding means configuration work still to do, so an unanswered
 * requirement is not outstanding — nobody can configure it until somebody
 * answers it. Reported separately.
 */
export const asDomainTotals = (counts: StatusCounts): DomainTotals => ({
  total: counts.total,
  outstanding: counts.dirty + counts.proposed,
  unanswered: counts.outOfScope,
});

/** Domain sections in the backend's own order, unknown domains last. */
export const groupByDomain = (requirements: Requirement[]): DomainGroup[] => {
  const byDomain = new Map<string | null, Requirement[]>();
  for (const requirement of requirements) {
    const domain = requirement.group.domain ?? null;
    byDomain.set(domain, [...(byDomain.get(domain) ?? []), requirement]);
  }

  const rank = (domain: string | null) => {
    if (domain === null) return DOMAIN_ORDER.length + 1;
    const index = DOMAIN_ORDER.indexOf(domain);
    return index === -1 ? DOMAIN_ORDER.length : index;
  };

  return [...byDomain.entries()]
    .map(([domain, items]) => ({ domain, requirements: items }))
    .sort((a, b) => rank(a.domain) - rank(b.domain));
};

/**
 * Outstanding leads where there is any, because that is the work. Where there
 * is none, an unanswered count still has to be said — otherwise a domain whose
 * every requirement is unanswered would read as finished.
 */
const summarise = ({ total, outstanding, unanswered }: DomainTotals) => {
  if (outstanding > 0) {
    return unanswered > 0
      ? `${outstanding} of ${total} outstanding · ${unanswered} unanswered`
      : `${outstanding} of ${total} outstanding`;
  }
  if (unanswered > 0) return `${unanswered} of ${total} unanswered`;
  return `${total} configured`;
};

export const DomainSection = ({
  section,
  totals,
  openRequirementId,
  onSelect,
}: {
  section: DomainGroup;
  totals: DomainTotals;
  openRequirementId: string | undefined;
  onSelect: (id: string) => void;
}) => (
  <DomainBlock>
    <DomainHeader>
      {/* The heading dot shows the most pressing thing in the section. */}
      <StatusDot
        $status={mostPressingStatus(
          section.requirements.map(
            (requirement) => requirement.status as RequirementStatus
          )
        )}
      />
      <Typography variant={TypographyVariant.bodyMd2}>
        {domainLabel(section.domain)}
      </Typography>
      <Muted>{summarise(totals)}</Muted>
    </DomainHeader>
    <Stack $gap={1}>
      {section.requirements.map((requirement) => (
        <RequirementRow
          key={requirement.id}
          type="button"
          $selected={requirement.id === openRequirementId}
          onClick={() => onSelect(requirement.id)}
        >
          <span>
            <Typography variant={TypographyVariant.bodySm1}>
              {requirement.name}
            </Typography>
            <Answer $missing={!requirement.body}>
              {requirement.body || 'Not answered yet'}
            </Answer>
          </span>
          <StatusDot $status={requirement.status as RequirementStatus} />
        </RequirementRow>
      ))}
    </Stack>
  </DomainBlock>
);

const DomainBlock = styled.div`
  ${({ theme }) => css`
    background: ${theme.palette.neutral['00']};
    border: 1px solid ${theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing(3)};
    display: grid;
    gap: ${theme.spacing(2)};
  `}
`;

const DomainHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `}
`;

const RequirementRow = styled.button<{ $selected: boolean }>`
  ${({ theme, $selected }) => css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.spacing(3)};
    width: 100%;
    text-align: left;
    background: ${$selected
      ? theme.palette.neutral[10]
      : theme.palette.neutral['00']};
    border: 1px solid
      ${$selected ? theme.palette.primary.main : theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.sm};
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
    cursor: pointer;

    &:hover {
      border-color: ${theme.palette.primary.main};
    }

    > span:first-child {
      display: grid;
      gap: 2px;
    }
  `}
`;

const Answer = styled.span<{ $missing: boolean }>`
  ${({ theme, $missing }) => css`
    color: ${$missing ? theme.palette.neutral[70] : theme.palette.neutral[90]};
    font-style: ${$missing ? 'italic' : 'normal'};
    font-size: 0.8125rem;
    line-height: 1.4;
  `}
`;
